import { VITAMINS_AND_SUPPLEMENTS } from 'utils/constants/vitaminsAndSupplements';
import { MembershipStatus, ProductsBySlugType } from '../types';
import { UserType } from './useUserContext/types';

type MembershipVariant = Record<string, number>;

export const controlMembershipVariants: MembershipVariant = {
  'chlamydia-gonorrhea-test': 505,
  'syphilis-test': 506,
  'hepatitis-c-test': 507,
  'hiv-test': 508,
  'trichomoniasis-test': 509,
  hba1c: 510,
  'cholesterol-and-lipids-test': 511,
  'thyroid-test': 512,
  metabolism: 513,
  'testosterone-test': 514,
  'vitamin-d-test': 515,
  'vitamin-d-and-inflammation-test': 516,
  'fit-colon-cancer-screening-test': 517,
  'hpv-test-female': 518,
  'heart-health-test': 1071,
};
export const stiMembershipVariants: MembershipVariant = {
  'chlamydia-gonorrhea-test': 375,
  'syphilis-test': 376,
  'hepatitis-c-test': 377,
  'hiv-test': 378,
  'trichomoniasis-test': 379,
};

export type CreditData = {
  memberPrice?: string;
  memberHeroCopy?: string;
  promoText?: string;
  activeCredits?: number;
  membershipVariantId?: number;
};

function applyDiscount(
  variants: MembershipVariant,
  activeCredits: number,
  productsBySlug: ProductsBySlugType,
  membershipState?: MembershipStatus,
): Record<string, ProductsBySlugType[string] & CreditData> {
  Object.keys(productsBySlug).forEach((key: string) => {
    if (activeCredits > 0 && productsBySlug[key].slug in variants) {
      // If the product is a part of the membership and the user has available credits
      const creditAvailableData: CreditData = {
        memberPrice: 'Free',
        memberHeroCopy: 'Included with Membership',
        promoText: 'Included with Membership',
        activeCredits: activeCredits,
        membershipVariantId: variants[productsBySlug[key].slug],
      };
      productsBySlug[key] = {
        ...productsBySlug[key],
        ...creditAvailableData,
      };
    } else if (
      productsBySlug[key].slug &&
      typeof productsBySlug[key].slug == 'string' &&
      productsBySlug[key].slug.toLowerCase().includes('covid') === false &&
      membershipState === MembershipStatus.Active &&
      !VITAMINS_AND_SUPPLEMENTS.includes(productsBySlug[key].slug)
    ) {
      // All other non-covid tests get a 20% discount
      const memberPrice = parseFloat(productsBySlug[key].price) * 0.8;
      const membershipDiscountData = {
        memberPrice: `$${memberPrice.toFixed(0)}`,
        memberHeroCopy: 'Member Price (20% Off)',
      };
      productsBySlug[key] = {
        ...productsBySlug[key],
        ...membershipDiscountData,
      };
    } else {
      // Test doesn't change. Only COVID, for now.
      // noop
    }
  });
  return productsBySlug;
}

/**
 * This hook accepts a map of products (by slug) and returns the same object with adjusted
 * product data, according to whether a user has available credits, which tests are available
 * to them, and any other applicable discounts
 *
 * @param isMember whether the current user is a member at all
 * @param membershipType which membership the user has, if any
 * @param productsBySlug all products organized as Key: Slug, Value: ProductData
 * @returns
 */
const useMembershipProducts = (
  {
    isMember,
    membershipType,
    activeCredits,
    membershipState,
  }: Pick<
    UserType,
    'isMember' | 'membershipType' | 'activeCredits' | 'membershipState'
  >,
  productsBySlug: ProductsBySlugType,
): Record<string, ProductsBySlugType[string] & CreditData> => {
  if (isMember === true) {
    if (membershipType === 'control') {
      return applyDiscount(
        controlMembershipVariants,
        activeCredits,
        productsBySlug,
        membershipState,
      );
    } else if (membershipType === 'sti') {
      return applyDiscount(
        stiMembershipVariants,
        activeCredits,
        productsBySlug,
        membershipState,
      );
    }
  }

  return productsBySlug;
};

export default useMembershipProducts;
